import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    IconButton,
    Button,
    TextField,
    RadioGroup,
    FormControlLabel,
    Radio,
    Checkbox,
    FormGroup,
    Paper,
    Grid,
    CircularProgress,
    AppBar,
    Toolbar
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import type { RootState } from '../store';
import { useSelector, useDispatch } from 'react-redux';
import { store } from '../store';
import api from '../services/api';

import { addOrderItemAsync, updateOrderItemAsync, getOrCreateCurrentOrder } from '../store/slices/orderSlice';
import type { OrderItem } from '../store/slices/orderSlice';
import type { AppDispatch } from '../store';
import { clearRecommendationsForParent } from '../store/slices/recommendedItemsSlice';
import CustomizeItemSkeleton from '../components/skeletons/CustomizeItemSkeleton';
import RecommendedItems from '../components/RecommendedItems';
import type { MenuItem, MenuItemResponse, SelectedOption, SelectedOptions, MenuItemOptionGroup } from '../types/menu';

interface CustomizableMenuItem extends MenuItem {
    options?: MenuItemOptionGroup[];
}

const CustomizeItemPage: React.FC = () => {
    const { itemId, orderId, orderItemId } = useParams<{ itemId?: string; orderId?: string; orderItemId?: string }>();
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const location = useLocation();

    const [item, setItem] = useState<CustomizableMenuItem | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [quantity, setQuantity] = useState(1);
    const [selectedOptions, setSelectedOptions] = useState<SelectedOptions>({});
    const [specialInstructions, setSpecialInstructions] = useState('');
    const [totalPrice, setTotalPrice] = useState(0);
    const [editingCartItemId, setEditingCartItemId] = useState<string | null>(null);
    const [isAddingToCart, setIsAddingToCart] = useState(false);


    const currentOrder = useSelector((state: RootState) => state.order.currentOrder);
    const orderFromReadyToPay = location.state?.cartItem?.orderId;
    const menuScrollState = useSelector((state: RootState) => state.ui.menuScrollState);

    // Initialize order if it doesn't exist
    const initializeOrderIfNeeded = async () => {
        if (!currentOrder) {
            // Try to get tableId from localStorage or URL params
            const tableId = localStorage.getItem('tableId');
            if (tableId) {
                try {
                    await dispatch(getOrCreateCurrentOrder(tableId)).unwrap();
                } catch (error) {
                    console.error('Failed to initialize order:', error);
                    // If we can't initialize an order, redirect to error or home
                    navigate('/error');
                }
            } else {
                console.error('No table ID found to initialize order');
                navigate('/error');
            }
        }
    };

    // Initialize order on component mount if needed
    useEffect(() => {
        initializeOrderIfNeeded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Price calculation function
    const calculateTotalPrice = (item: CustomizableMenuItem | null, selectedOptions: SelectedOptions, quantity: number) => {
        if (!item) return 0;
        let currentPrice = Number(item.price || 0);
        Object.entries(selectedOptions).forEach(([groupName, choices]) => {
            choices.forEach(choice => {
                currentPrice += typeof choice.price === 'number' ? choice.price : 0;
            });
        });
        return currentPrice * quantity;
    };

    useEffect(() => {
        const fetchItem = async () => {
            if (!itemId && !orderItemId) {
                setError('Item ID is missing.');
                setLoading(false);
                return;
            }
            setLoading(true);
            try {
                let cartItemFromBackend: any = null;
                let menuItemIdToFetch = itemId;
                // If editing, fetch the order item from backend
                if (orderId && orderItemId) {
                    setEditingCartItemId(orderItemId);
                    const orderItemResp = await api.get(`/orders/${orderId}/items/${orderItemId}`);
                    cartItemFromBackend = orderItemResp.data;
                    menuItemIdToFetch = cartItemFromBackend.menuItemId;
                }
                const response = await api.get<MenuItemResponse>(`/menu/items/${menuItemIdToFetch}`);
                const rawData = response.data;

                const transformedAndSanitizedOptions: MenuItemOptionGroup[] = Array.isArray(rawData.optionGroups) ? 
                    rawData.optionGroups
                        .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
                        .map(group => ({
                            name: group.name,
                            required: group.required,
                            multiple: group.type === 'checkbox',
                            maxSelections: group.maxSelections || null,
                            displayOrder: group.displayOrder,
                            choices: Array.isArray(group.options) ? 
                                group.options
                                    .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
                                    .map(opt => ({
                                        name: opt.name,
                                        price: Number(opt.priceDelta || 0),
                                        displayOrder: opt.displayOrder
                                    })) : []
                        })) : [];

                const sanitizedItem: CustomizableMenuItem = {
                    ...rawData,
                    price: Number(rawData.price || 0),
                    startTime: rawData.startTime,
                    endTime: rawData.endTime,
                    daysOfWeek: rawData.daysOfWeek,
                    options: transformedAndSanitizedOptions
                };
                setItem(sanitizedItem);

                // Initialize selectedOptions
                let initialSelectedOptions: SelectedOptions = {};
                if (cartItemFromBackend && cartItemFromBackend.options) {
                    // Use options from backend
                    initialSelectedOptions = cartItemFromBackend.options;
                } else if (sanitizedItem.options) {
                    sanitizedItem.options.forEach(group => {
                        if (group.required && group.choices.length > 0) {
                            initialSelectedOptions[group.name] = [{
                                name: group.choices[0].name,
                                price: group.choices[0].price || 0
                            }];
                        } else {
                            initialSelectedOptions[group.name] = [];
                        }
                    });
                }
                setSelectedOptions(initialSelectedOptions);
                setQuantity(cartItemFromBackend?.quantity || 1);
                setSpecialInstructions(cartItemFromBackend?.notes || '');
                setTotalPrice(calculateTotalPrice(sanitizedItem, initialSelectedOptions, cartItemFromBackend?.quantity || 1));
            } catch (err: any) {
                console.error('Failed to fetch item for customization:', err);
                setError(err.message || 'Failed to load item details.');
                navigate('/error');
            } finally {
                setLoading(false);
            }
        };
        fetchItem();
    }, [itemId, orderId, orderItemId, dispatch]);

    useEffect(() => {
        const state = location.state as { cartItem?: OrderItem & { menuItem?: MenuItem, notes?: string, selectedOptions?: SelectedOptions } } | null;
        const cartItemFromState = state?.cartItem;

        if (cartItemFromState && item) {
            if (cartItemFromState.id) {
                setEditingCartItemId(cartItemFromState.id);
            }
            if (typeof cartItemFromState.quantity === 'number') {
                setQuantity(cartItemFromState.quantity);
            }
            if (typeof cartItemFromState.notes === 'string') {
                setSpecialInstructions(cartItemFromState.notes);
            }

            if (cartItemFromState.selectedOptions && item.options) {
                const newSelectedOptionsFromCart: SelectedOptions = {};
                for (const [groupName, choicesInCart] of Object.entries(cartItemFromState.selectedOptions)) {
                    if (choicesInCart && choicesInCart.length > 0) {
                        newSelectedOptionsFromCart[groupName] = choicesInCart.map(choice => ({
                            name: choice.name,
                            price: choice.price
                        }));
                    }
                }
                setSelectedOptions(prevSelectedOptions => ({
                    ...prevSelectedOptions,
                    ...newSelectedOptionsFromCart
                }));
            }
        }
    }, [item, location.state]);

    useEffect(() => {
        setTotalPrice(calculateTotalPrice(item, selectedOptions, quantity));
    }, [item, selectedOptions, quantity]);

    const handleOptionChange = (groupName: string, choice: SelectedOption) => {
        setSelectedOptions(prevSelectedOptions => {
            const currentChoices = prevSelectedOptions[groupName] || [];
            const optionGroup = item?.options?.find(og => og.name === groupName);
            
            if (!optionGroup) return prevSelectedOptions;

            const newOptions: SelectedOptions = { ...prevSelectedOptions };

            if (optionGroup.multiple) {
                // For multiple choice options (checkboxes)
                const isSelected = currentChoices.some(c => c.name === choice.name);
                if (isSelected) {
                    // Remove the option if already selected
                    newOptions[groupName] = currentChoices.filter(c => c.name !== choice.name);
                } else {
                    // Check if we can add more options based on maxSelections
                    if (optionGroup.maxSelections && currentChoices.length >= optionGroup.maxSelections) {
                        // Show a toast or alert to inform the user
                        alert(`Puedes seleccionar máximo ${optionGroup.maxSelections} opciones para "${groupName}"`);
                        return prevSelectedOptions; // Don't change the selection
                    }
                    // Add the new option
                    newOptions[groupName] = [...currentChoices, choice];
                }
            } else {
                // For single choice options (radio buttons)
                // Find the option in the menu item to get its price
                const option = optionGroup.choices.find(c => c.name === choice.name);
                if (option) {
                    newOptions[groupName] = [{
                        name: choice.name,
                        price: option.price
                    }];
                }
            }

            return newOptions;
        });
    };

    const isOptionSelected = (groupName: string, choiceName: string) => {
        const choices = selectedOptions[groupName] || [];
        return choices.some(choice => choice.name === choiceName);
    };

    const getSelectedOptionsForGroup = (groupName: string) => {
        return selectedOptions[groupName] || [];
    };

    const handleQuantityChange = (delta: number) => {
        setQuantity(prev => Math.max(1, prev + delta));
    };

    const handleSaveItemToCart = async () => {
        if (!item) {
            console.error('No item selected');
            alert('Error: No hay artículo seleccionado.');
            return;
        }

        // Get the current order, trying to initialize if needed
        let orderToUse = currentOrder;
        if (!orderToUse) {
            console.error('No current order available');
            // Try to initialize the order one more time
            try {
                await initializeOrderIfNeeded();
                // Get the fresh order from store after initialization
                orderToUse = store.getState().order.currentOrder;
                if (!orderToUse) {
                    alert('Error: No se pudo crear una orden. Por favor, regresa al menú principal.');
                    navigate(-1);
                    return;
                }
            } catch (error) {
                console.error('Failed to initialize order during save:', error);
                alert('Error: No se pudo crear una orden. Por favor, regresa al menú principal.');
                navigate(-1);
                return;
            }
        }

        setIsAddingToCart(true);
        try {
            const formattedOptions: SelectedOptions = Object.entries(selectedOptions).reduce((acc, [groupName, choices]) => {
                acc[groupName] = choices.map(choice => ({
                    name: choice.name
                }));
                return acc;
            }, {} as SelectedOptions);

            if (editingCartItemId) { // UPDATE existing item
                const updates = {
                    quantity,
                    notes: specialInstructions,
                    selectedOptions: formattedOptions,
                    price: totalPrice / quantity,
                };
                await dispatch(updateOrderItemAsync({ orderId: orderToUse.id, itemId: editingCartItemId, updates })).unwrap();
                navigate(-1); // Go back after update
            } else { // ADD new item
                const orderItemData = {
                    id: `${item.id}-${Date.now()}`,
                    menuItemId: item.id,
                    menuItem: item,
                    name: item.name,
                    quantity,
                    price: totalPrice / quantity,
                    notes: specialInstructions,
                    selectedOptions: formattedOptions,
                };
                await dispatch(addOrderItemAsync({ orderId: orderToUse.id, item: orderItemData })).unwrap();
                
                // Clear recommendations for this parent item when main product is added to cart
                dispatch(clearRecommendationsForParent(item.id));
                
                navigate(-1); // Go back after adding
            }
        } catch (error) {
            console.error('Error saving item to cart:', error);
            alert('Ocurrió un error al guardar el artículo. Por favor intenta de nuevo.');
        } finally {
            setIsAddingToCart(false);
        }
    };

    if (loading) {
        return <CustomizeItemSkeleton />;
    }

    if (error) {
        return null;
    }

    if (!item) {
        return null;
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: 'background.default' }}>
            {/* Custom AppBar */}
            <AppBar position="sticky" sx={{ bgcolor: 'background.paper', boxShadow: '0 2px 4px -1px rgba(0,0,0,0.06), 0 4px 5px 0 rgba(0,0,0,0.04), 0 1px 10px 0 rgba(0,0,0,0.03)', borderRadius: 0 }}>
                <Toolbar>
                    <IconButton edge="start" aria-label="atrás" onClick={() => navigate(-1)} sx={{ color: 'text.primary' }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h6" component="div" sx={{ flexGrow: 1, color: 'text.primary', textAlign: 'center' }}>
                        {item.name}
                    </Typography>
                    {/* Optional: A placeholder or an icon on the right for balance */}
                    <Box sx={{ width: 40 }} />
                </Toolbar>
            </AppBar>

            {/* Scrollable Content Area */}
            <Box sx={{ flexGrow: 1, overflowY: 'auto', pb: '100px' /* Space for bottom bar */ }}>
                <Container maxWidth="sm" sx={{ pt: 1, pb: 2, px: 0 }}>
                    {item.imageUrl && (
                        <Box sx={{ width: '100%', height: 250, overflow: 'hidden', mb: 2 }}>
                            <img src={item.imageUrl} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </Box>
                    )}
                    <Box sx={{ px: 2 }}>
                        <Typography variant="h5" component="h1" fontWeight="bold" color="text.primary" sx={{ mb: 0 }}>
                            {item.name}
                        </Typography>
                        <Typography variant="h5" component="h1" fontWeight="bold" color="primary" sx={{ mb: 1 }}>
                            ₡{Number(item.price).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {item.description || 'No hay descripción disponible.'}
                        </Typography>

                        {item.options && (item.options as any[]).map((group: any) => (
                            <Box key={group.name} sx={{ mb: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography variant="h6" fontWeight="medium" color="text.primary">{group.name}</Typography>
                                    {group.multiple && group.maxSelections && (
                                        <Typography variant="caption" color="text.secondary">
                                            {getSelectedOptionsForGroup(group.name).length}/{group.maxSelections} seleccionados
                                        </Typography>
                                    )}
                                </Box>
                                <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
                                    {group.multiple ? (
                                        <FormGroup>
                                            {group.choices.map((choice: any) => (
                                                <FormControlLabel
                                                    key={choice.name}
                                                    control={
                                                        <Checkbox
                                                            checked={isOptionSelected(group.name, choice.name)}
                                                            onChange={() => handleOptionChange(group.name, choice)}
                                                            disabled={group.maxSelections && 
                                                                !isOptionSelected(group.name, choice.name) && 
                                                                getSelectedOptionsForGroup(group.name).length >= group.maxSelections}
                                                        />
                                                    }
                                                    label={`${choice.name}${choice.price > 0 ? ` (+₡${Number(choice.price).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })})` : ''}`}
                                                />
                                            ))}
                                        </FormGroup>
                                    ) : (
                                        <RadioGroup
                                            value={getSelectedOptionsForGroup(group.name)[0]?.name || (group.required && group.choices.length > 0 ? group.choices[0].name : '')}
                                            onChange={(e) => handleOptionChange(group.name, { name: e.target.value, price: 0 })}
                                        >
                                            {group.choices.map((choice: any) => (
                                                <FormControlLabel
                                                    key={choice.name}
                                                    value={choice.name}
                                                    control={<Radio />}
                                                    label={`${choice.name}${choice.price > 0 ? ` (+₡${Number(choice.price).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })})` : ''}`}
                                                />
                                            ))}
                                        </RadioGroup>
                                    )}
                                </Paper>
                            </Box>
                        ))}

                        <Typography variant="h6" fontWeight="medium" gutterBottom sx={{ mt: 3 }} color="text.primary">Instrucciones Especiales</Typography>
                        <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                variant="outlined"
                                placeholder="ej., sin pepinillos, bien cocido..."
                                value={specialInstructions}
                                onChange={(e) => setSpecialInstructions(e.target.value)}
                            />
                        </Paper>

                        {/* Recommended Items Section */}
                        <RecommendedItems 
                            currentItemId={item.id}
                            currentItemName={item.name}
                            isEditing={!!editingCartItemId}
                            selectedCategory={menuScrollState?.selectedCategory || 0}
                            onItemAdded={() => {
                                // Optional: You can add any additional logic here
                                // like showing a success message or updating cart count
                            }}
                        />
                    </Box>
                </Container>
            </Box>

            {/* Bottom Action Bar */}
            <Paper
                elevation={3}
                sx={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    p: 2,
                    borderTop: '1px solid #e0e0e0',
                    bgcolor: 'background.paper',
                    zIndex: 1100, // Ensure it's above content
                    borderRadius: 0
                }}
            >
                <Grid container alignItems="center" spacing={2}>
                    <Grid item xs={4} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <IconButton onClick={() => handleQuantityChange(-1)} disabled={quantity <= 1} size="small" sx={{ width: 32, height: 32 }}>
                            <RemoveIcon />
                        </IconButton>
                        <Typography 
                          data-testid="quantity-display"
                          variant="h6" 
                          component="span" 
                          sx={{ px: 2 }}
                        >
                            {quantity}
                        </Typography>
                        <IconButton 
                          data-testid="increase-quantity-button"
                          onClick={() => handleQuantityChange(1)} 
                          size="small" 
                          sx={{ width: 32, height: 32 }}
                        >
                            <AddIcon />
                        </IconButton>
                    </Grid>
                    <Grid item xs={8}>
                        <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            size="medium"
                            onClick={handleSaveItemToCart}
                            disabled={isAddingToCart}
                            sx={{ 
                                textTransform: 'none',
                                py: '10px',
                                fontWeight: 'bold',
                                fontSize: '0.85rem', 
                                lineHeight: 1.2, 
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {isAddingToCart ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : (
                                `${editingCartItemId ? 'Actualizar' : 'Agregar'} ${quantity} (₡${Number(totalPrice).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })})`
                            )}
                        </Button>
                    </Grid>
                </Grid>
            </Paper>
        </Box>
    );
};

export default CustomizeItemPage;